import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { registerSW } from 'virtual:pwa-register'

import { m } from '@/lib/paraglide/messages'

// The update poll below runs on factory wifi that drops out and behind an edge
// proxy that intermittently answers the sw.js fetch with a 502. Chrome rejects
// `registration.update()` with this TypeError, and left unhandled it lands in
// Sentry as an application error — hundreds of events for a condition the very
// next poll (60s later) fixes on its own. Demote it to a breadcrumb: no issue
// of its own, but still on the trail leading into whatever real error follows.
const FAILED_UPDATE_POLL = /Failed to update a ServiceWorker/i

export async function checkForServiceWorkerUpdate(
    registration: ServiceWorkerRegistration,
): Promise<void> {
    try {
        await registration.update()
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (!FAILED_UPDATE_POLL.test(message)) {
            Sentry.captureException(err)
            return
        }
        Sentry.addBreadcrumb({
            category: 'pwa',
            level: 'info',
            message: `service worker update poll failed: ${message}`,
        })
    }
}

let registered = false
let reloadRequested = false
// 'waiting': a new worker is installed but not active yet; 'active': another
// tab already activated it, so this tab only needs to reload.
let pendingUpdate: 'none' | 'waiting' | 'active' = 'none'
let activateUpdate: (() => void) | undefined

// Poll for updates, but let each tab choose when to load the new build.
export function registerPwaUpdater(): void {
    if (typeof window === 'undefined') return
    if (!('serviceWorker' in navigator) || registered) return
    registered = true
    const hadController = !!navigator.serviceWorker.controller

    let reloading = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        // A tab with no controller at registration is a first install: the new
        // worker claiming it is not an update, so neither reload nor prompt.
        if (!hadController) return
        if (reloadRequested) {
            if (reloading) return
            reloading = true
            window.location.reload()
            return
        }
        // Another tab asked for the reload; this tab keeps its state and only
        // offers the same prompt.
        pendingUpdate = 'active'
        showUpdateToast()
    })

    const updateSW = registerSW({
        immediate: true,
        onRegisteredSW(_swUrl, registration) {
            if (!registration) return
            // Check for a new SW once per minute while the tab is open.
            setInterval(() => {
                void checkForServiceWorkerUpdate(registration)
            }, 60 * 1000)
        },
        onNeedRefresh() {
            pendingUpdate = 'waiting'
            showUpdateToast()
        },
    })

    activateUpdate = () => {
        reloadRequested = true
        void updateSW(true)
    }

    function showUpdateToast() {
        toast(m.pwa_update_available(), {
            id: 'pwa-update',
            duration: Infinity,
            action: {
                label: m.pwa_update_reload(),
                onClick: () => activateUpdate?.(),
            },
        })
    }

    window.addEventListener('vite:preloadError', (event) => {
        event.preventDefault()
        showUpdateToast()
    })
}

interface NavigationSource {
    subscribe: (
        event: 'onResolved',
        listener: (event: { pathChanged: boolean }) => void,
    ) => () => void
}

/**
 * Loads a pending build when the operator moves to another page. Tabs stay
 * open all day and the reload prompt gets ignored, so an old build would keep
 * calling an API that has since changed; leaving a page has already dropped
 * its state, so reloading there loses nothing. A search-param change (filter,
 * pagination) stays on the page and is left alone.
 */
export function applyUpdateOnNavigation(router: NavigationSource): () => void {
    return router.subscribe('onResolved', ({ pathChanged }) => {
        if (!pathChanged || reloadRequested) return
        if (pendingUpdate === 'waiting') {
            activateUpdate?.()
        } else if (pendingUpdate === 'active') {
            reloadRequested = true
            window.location.reload()
        }
    })
}
